/**
 * Cupos de red multinivel (cliente).
 * Alineado con functions/common/networkSlots.js
 */

import { fallbackTranslations } from './fallbackTranslations';

export const DIRECT_SLOTS_PER_LEVEL = 50;
export const MAX_NETWORK_LEVEL = 3;

/** Distribución del 16% destinado a la red (sobre el monto acreditado). */
export const NETWORK_SHARE_TOTAL_PERCENT = 16;
export const NETWORK_LEVEL_PERCENTS = {
  1: 8,
  2: 6,
  3: 2,
};
export const NETWORK_DISTRIBUTION_LABEL = '8% / 6% / 2% = 16%';

/** Techos MENSUALES de ganancia/retiro de red (COP). */
export const EARNINGS_CAP_LEADER = 50000000;
export const EARNINGS_CAP_STANDARD = 30000000;

import { formatCop } from './currencyFormat';
export { formatCop };

export function normalizeNetworkState(user, uid) {
  if (!user || user.usertype !== 'driver') {
    return { inNetwork: false };
  }
  let level = user.networkLevel;
  if (level == null || level === '') {
    if (user.signupMode === 'city_special') level = 0;
    else return { inNetwork: false };
  }
  level = Number(level);
  if (Number.isNaN(level) || level < 0) return { inNetwork: false };

  const total =
    level >= MAX_NETWORK_LEVEL
      ? 0
      : user.directSlotsTotal != null
        ? Number(user.directSlotsTotal)
        : DIRECT_SLOTS_PER_LEVEL;
  const used = Number(user.directSlotsUsed) || 0;
  const principalUid =
    user.principalUid || (level === 0 ? uid : null) || null;

  return {
    inNetwork: true,
    level,
    total,
    used,
    remaining: Math.max(0, total - used),
    principalUid,
    cityId: user.cityId || null,
    canRefer: level < MAX_NETWORK_LEVEL && used < total,
  };
}

export function isLeaderDriver(user, uid) {
  if (!user || user.usertype !== 'driver') return false;
  if (user.signupMode === 'city_special') return true;
  const net = normalizeNetworkState(user, uid);
  return !!(net.inNetwork && net.level === 0);
}

export function resolveEarningsCap(user, uid) {
  if (user && user.earningsCap != null && !Number.isNaN(Number(user.earningsCap))) {
    return Number(user.earningsCap);
  }
  return isLeaderDriver(user, uid) ? EARNINGS_CAP_LEADER : EARNINGS_CAP_STANDARD;
}

/** Mes calendario Bogotá: YYYY-MM */
export function getMonthKey(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  return `${y}-${m}`;
}

function monthlyField(user, keyField, amountField, monthKey) {
  if (!user) return 0;
  if (String(user[keyField] || '') !== String(monthKey)) return 0;
  return Number(user[amountField]) || 0;
}

export function getMonthlyEarningsUsed(user, monthKey = getMonthKey()) {
  return monthlyField(
    user,
    'networkEarningsMonthKey',
    'networkEarningsMonthAmount',
    monthKey
  );
}

export function getMonthlyWithdrawUsed(user, monthKey = getMonthKey()) {
  return monthlyField(
    user,
    'networkWithdrawMonthKey',
    'networkWithdrawMonthAmount',
    monthKey
  );
}

/** Días de retiro de bono de red (calendario Bogotá). */
export const WITHDRAW_PAYDAYS = [15, 30];

/** Monto mínimo por retiro de bono de red (COP). Debe coincidir con functions/common/networkSlots.js */
export const MIN_REFERRAL_WITHDRAW = 100000;

/** Fee de plataforma sobre cada retiro de bono (%). Debe coincidir con functions/common/networkSlots.js */
export const REFERRAL_WITHDRAW_FEE_PERCENT = 5;

/** Se descuenta del monto solicitado: del bono sale `gross`, el conductor recibe `net`. */
export function computeReferralWithdrawFee(amount) {
  const gross = Math.round(Number(amount) || 0);
  const fee = Math.round((gross * REFERRAL_WITHDRAW_FEE_PERCENT) / 100);
  return { gross, feePercent: REFERRAL_WITHDRAW_FEE_PERCENT, fee, net: gross - fee };
}

/** Día del mes en zona América/Bogotá. */
export function getBogotaDay(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    day: '2-digit',
  }).formatToParts(date);
  return Number(parts.find((p) => p.type === 'day')?.value) || 0;
}

/** Días de retiro del mes (Bogotá): 15 y 30, o el último día si el mes no tiene 30 (febrero). */
export function getWithdrawPaydays(date = new Date()) {
  const [y, m] = getMonthKey(date).split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return WITHDRAW_PAYDAYS.map((d) => Math.min(d, lastDay));
}

/** "15 y 30" (o "15 y 28" en febrero). */
export function formatWithdrawPaydays(date = new Date()) {
  return getWithdrawPaydays(date).join(' y ');
}

/**
 * Clave del día de retiro: YYYY-MM-15 | YYYY-MM-30 (último día en febrero).
 * null si hoy no es día de retiro.
 */
export function getWithdrawPaydayKey(date = new Date()) {
  const day = getBogotaDay(date);
  if (!getWithdrawPaydays(date).includes(day)) return null;
  return `${getMonthKey(date)}-${String(day).padStart(2, '0')}`;
}

/** Tope por día de retiro = la mitad del techo mensual (25M / 15M). */
export function getPaydayWithdrawCap(user, uid) {
  const monthly = resolveEarningsCap(user, uid);
  return Math.floor(monthly / 2);
}

export function getPaydayWithdrawUsed(user, paydayKey) {
  if (!user || !paydayKey) return 0;
  if (String(user.networkWithdrawPaydayKey || '') !== String(paydayKey)) return 0;
  return Number(user.networkWithdrawPaydayAmount) || 0;
}

/** Progreso del tope MENSUAL de generación de bonos de red. */
export function formatEarningsCapProgress(user, uid) {
  const cap = resolveEarningsCap(user, uid);
  const monthKey = getMonthKey();
  const used = getMonthlyEarningsUsed(user, monthKey);
  const remaining = Math.max(0, cap - used);
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  return {
    cap,
    used,
    remaining,
    pct,
    monthKey,
    period: 'monthly',
    label: `${formatCop(used)} / ${formatCop(cap)} (mes)`,
    type: user?.earningsCapType || (isLeaderDriver(user, uid) ? 'leader' : 'standard'),
  };
}

/**
 * Cupo de retiro del día (15 o 30): mitad del techo mensual.
 * Líder 25M+25M; estándar 15M+15M.
 */
export function formatWithdrawCapProgress(user, uid, date = new Date()) {
  const monthlyCap = resolveEarningsCap(user, uid);
  const paydayCap = getPaydayWithdrawCap(user, uid);
  const paydayKey = getWithdrawPaydayKey(date);
  const day = getBogotaDay(date);
  const isPayday = !!paydayKey;
  const used = isPayday ? getPaydayWithdrawUsed(user, paydayKey) : 0;
  const remaining = isPayday ? Math.max(0, paydayCap - used) : 0;
  const balance = Number(user?.walletReferral) || 0;
  const withdrawable = isPayday ? Math.min(balance, remaining) : 0;
  const pct =
    paydayCap > 0 ? Math.min(100, Math.round((used / paydayCap) * 100)) : 0;
  const monthUsed = getMonthlyWithdrawUsed(user, getMonthKey(date));
  return {
    cap: paydayCap,
    monthlyCap,
    used,
    remaining,
    withdrawable,
    balance,
    pct,
    monthKey: getMonthKey(date),
    paydayKey,
    payday: day,
    isPayday,
    monthUsed,
    period: 'payday',
    label: isPayday
      ? `${formatCop(used)} / ${formatCop(paydayCap)} (día ${day})`
      : `Retiros solo días ${formatWithdrawPaydays(date)} · cupo ${formatCop(paydayCap)} c/u`,
    type: user?.earningsCapType || (isLeaderDriver(user, uid) ? 'leader' : 'standard'),
  };
}

export function networkLevelLabel(level) {
  if (level === 0 || level === '0') return 'Principal (Líder)';
  if (level == null || level === '') return '—';
  return `Nivel ${level}`;
}

/** Estados de estructura persistidos en el usuario al registrarse. */
export const NETWORK_STRUCTURE_STATUS = {
  LEADER: 'leader',
  NETWORK: 'network',
  UNSTRUCTURED: 'unstructured',
  INDEPENDENT: 'independent',
  CUSTOMER: 'customer',
  CUSTOMER_NETWORK: 'customer_network',
};

export function networkStructureStatusLabel(status) {
  switch (status) {
    case 'leader':
      return 'Líder (cupo ciudad)';
    case 'network':
      return 'En red multinivel';
    case 'unstructured':
      return 'Referido fuera de red';
    case 'independent':
      return 'Independiente';
    case 'customer_network':
      return 'Usuario en red';
    case 'customer':
      return 'Usuario';
    default:
      return status || '—';
  }
}

export function formatDirectSlots(used, total) {
  const u = Number(used) || 0;
  const t = total == null ? DIRECT_SLOTS_PER_LEVEL : Number(total);
  if (t <= 0) return 'Sin cupos';
  return `${u} / ${t}`;
}

/** Cupos de los primeros 100 por ciudad: la barra sigue a líderes reales, no a slotsUsed desfasado. */
export const CITY_LEADER_SLOTS_TOTAL = 100;

/**
 * @param {{ slotsUsedStored?: number, slotsTotal?: number, leadersCount?: number }} p
 * @returns {{ slotsTotal: number, slotsUsed: number, slotsAvailable: number, storedSlotsUsed: number, drifted: boolean }}
 */
export function resolveCitySlotUsage({
  slotsUsedStored = 0,
  slotsTotal = CITY_LEADER_SLOTS_TOTAL,
  leadersCount = 0,
} = {}) {
  const total =
    Number.isFinite(Number(slotsTotal)) && Number(slotsTotal) > 0
      ? Math.floor(Number(slotsTotal))
      : CITY_LEADER_SLOTS_TOTAL;
  const leaders = Math.max(0, Math.floor(Number(leadersCount) || 0));
  const used = Math.max(0, Math.min(total, leaders));
  const stored = Math.max(0, Math.floor(Number(slotsUsedStored) || 0));
  return {
    slotsTotal: total,
    slotsUsed: used,
    slotsAvailable: Math.max(0, total - used),
    storedSlotsUsed: stored,
    drifted: stored !== used,
  };
}

/**
 * Normaliza códigos pegados desde WhatsApp/SMS (guiones tipográficos, espacios invisibles).
 * Ej: "BAR‑MAESTRO‑BB9P" → "BAR-MAESTRO-BB9P"
 */
export function normalizeSignupCode(raw) {
  return String(raw || '')
    .normalize('NFKC')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
    .trim()
    .toUpperCase();
}

/** Parece código de zonificación (maestro o individual), no referralId corto de usuario. */
export function looksLikeCityZoningCode(raw) {
  const code = normalizeSignupCode(raw);
  if (!code) return false;
  if (code.includes('MAESTRO')) return true;
  // Prefijo ciudad 2–4 letras + guion + chunk (ej. BAR-AB12CD, MED-9WB2…)
  return /^[A-Z]{2,4}-[A-Z0-9]{4,12}$/.test(code);
}

/** Códigos de error de validate_city_code / user_signup → clave de traducción. */
const SIGNUP_ERROR_KEYS = {
  slots_full: 'signup_err_slots_full',
  slots_full_use_normal_referral: 'signup_err_slots_full',
  network_slots_full: 'signup_err_network_slots_full',
  network_level_cannot_refer: 'signup_err_network_level',
  city_mismatch: 'signup_err_city_mismatch',
  referrer_not_found: 'signup_err_referrer_not_found',
  city_code_required: 'signup_err_city_code_required',
  referral_code_required: 'signup_err_referral_code_required',
  city_required: 'signup_err_city_required',
  missing_city: 'signup_err_city_required',
  city_inactive: 'signup_err_city_inactive',
  invalid_code: 'signup_err_invalid_code',
  missing_city_code: 'signup_err_invalid_code',
  missing_params: 'signup_err_invalid_code',
  code_used: 'signup_err_code_used',
  city_slot_claim_failed: 'signup_err_claim_failed',
  city_code_claim_failed: 'signup_err_claim_failed',
  network_claim_failed: 'signup_err_claim_failed',
  city_code_needs_driver: 'signup_err_city_code_needs_driver',
  city_code_needs_city: 'signup_err_city_code_needs_city',
  'User Not Created': 'reg_error',
};

/**
 * Mensaje traducido para un error de registro. Sin `t` devuelve el texto en español.
 * Con `fallbackKey`, los códigos desconocidos usan esa clave en vez de devolver null;
 * los errores que ya son una frase (traen espacios) se muestran tal cual.
 */
export function networkSignupErrorMessage(errorCode, t, fallbackKey) {
  let key = SIGNUP_ERROR_KEYS[errorCode];
  if (!key) {
    if (!fallbackKey) return null;
    if (typeof errorCode === 'string' && /\s/.test(errorCode.trim())) return errorCode;
    key = fallbackKey;
  }
  const msg = t ? t(key) : fallbackTranslations.es[key];
  return msg && msg !== key ? msg : fallbackTranslations.es[key] || null;
}
